import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  Button,
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerTitle,
  Input,
  RadioGroup,
  RadioGroupCardItem,
  VisuallyHidden,
  cn,
  useRadioGroupContainerStyles,
  PhoneInput,
  useFormValidation,
} from "@porchpass/ui";
import { X, Zap } from "lucide-react";
import { useEffect, useState } from "react";
import { z } from "zod";
import DeactivateEmployeeDialog from "./DeactivateEmployeeDialog";
import ProfilePhotoDialog from "./ProfilePhotoDialog";
import type { EditEmployeeFormData, Employee } from "src/types/employee";
import { timeAgo } from "src/utils";

// Zod schema for form validation
const editEmployeeSchema = z.object({
  first_name: z.string().min(1, "First name is required"),
  last_name: z.string().min(1, "Last name is required"),
  email: z
    .string()
    .min(1, "Email is required")
    .email("Please enter a valid email address"),
  phone_number: z.string().optional(),
  title: z.string().min(1, "Title is required"),
  role: z.enum(["SALES_REPRESENTATIVE", "GENERAL_MANAGER"], {
    errorMap: () => ({ message: "Role is required" }),
  }),
});

// Form state type that allows empty role
type EditEmployeeFormState = {
  first_name: string;
  last_name: string;
  email: string;
  phone_number?: string;
  title: string;
  role: "SALES_REPRESENTATIVE" | "GENERAL_MANAGER" | "";
} & Record<string, unknown>;

const SalesRepresentativeIcon = () => (
  <div className="flex relative justify-center items-center w-8 h-8">
    <svg
      width="32"
      height="32"
      viewBox="0 0 32 32"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className="absolute inset-0"
      preserveAspectRatio="none"
      style={{ overflow: "visible", display: "block" }}
    >
      <path
        id="star-1"
        d="M9.73913 4.35907C12.0087 -1.45302 19.9913 -1.45302 22.2609 4.35907C22.8338 5.8262 24.1735 6.82697 25.7038 6.93368C31.7564 7.35573 34.2371 15.1763 29.5766 19.1807C28.397 20.1943 27.8856 21.8159 28.2612 23.3511C29.745 29.4171 23.2731 34.2366 18.131 30.9167C16.8303 30.077 15.1697 30.077 13.869 30.9167C8.72694 34.2366 2.25495 29.4171 3.73885 23.3511C4.11441 21.8159 3.60304 20.1943 2.42344 19.1807C-2.23709 15.1763 0.243611 7.35573 6.29618 6.93368C7.82648 6.82697 9.16623 5.8262 9.73913 4.35907Z"
        fill="white"
      />
    </svg>
    <svg
      width="18"
      height="18"
      viewBox="0 0 16.125 16.125"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className="relative z-10"
    >
      <g>
        <path
          opacity="0.4"
          d="M9.26172 10.125C10.8092 10.125 12.2381 10.9542 13.0059 12.2979L13.7705 13.7568C12.311 15.2196 10.2921 16.125 8.0625 16.125C5.83301 16.1249 3.81491 15.2195 2.35547 13.7568L3.12012 12.2979C3.88795 10.9543 5.31676 10.125 6.86426 10.125H9.26172ZM8.0625 3.5625C9.51225 3.5625 10.6875 4.73775 10.6875 6.1875C10.6875 7.63725 9.51225 8.8125 8.0625 8.8125C6.61275 8.8125 5.4375 7.63725 5.4375 6.1875C5.4375 4.73775 6.61275 3.5625 8.0625 3.5625Z"
          fill="#226392"
        />
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M8.0625 1.46591C4.4193 1.46591 1.46591 4.4193 1.46591 8.0625C1.46591 11.7057 4.4193 14.6591 8.0625 14.6591C11.7057 14.6591 14.6591 11.7057 14.6591 8.0625C14.6591 4.4193 11.7057 1.46591 8.0625 1.46591ZM0 8.0625C0 3.6097 3.6097 0 8.0625 0C12.5153 0 16.125 3.6097 16.125 8.0625C16.125 12.5153 12.5153 16.125 8.0625 16.125C3.6097 16.125 0 12.5153 0 8.0625Z"
          fill="#226392"
        />
      </g>
    </svg>
  </div>
);

const GeneralManagerIcon = () => {
  const clipId = `general-manager-clip-${Math.random().toString(36).substr(2, 9)}`;
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 15.75 16.5"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className="w-[18px] h-[18px]"
    >
      <g clipPath={`url(#${clipId})`}>
        <path
          opacity="0.4"
          d="M6.78269 0.197155C6.78269 0.197155 8.78033 0.175549 8.96738 0.19698C9.1758 0.220861 9.39503 0.276089 9.59955 0.424024C9.804 0.571892 9.92535 0.762885 10.0137 0.953445C10.093 1.1246 10.4177 2.09732 10.4929 2.32351C10.5983 2.62135 10.985 3.16742 11.6885 2.96897C11.9345 2.89903 13.0912 2.57216 13.2892 2.54066C13.5096 2.50563 13.7507 2.50116 13.9999 2.60268C14.249 2.70416 14.4185 2.87579 14.552 3.05477L15.6244 4.91441C15.7084 5.10857 15.7704 5.32778 15.7439 5.58058C15.7173 5.83341 15.6112 6.03493 15.4888 6.20736C15.4122 6.31523 14.9392 6.85101 14.6051 7.22647C14.4596 7.38994 14.3405 7.52303 14.2919 7.57718C14.1033 7.81455 13.8393 8.41605 14.2918 8.92275C14.4522 9.10125 15.3788 10.1377 15.4888 10.2926C15.6112 10.465 15.7173 10.6665 15.7439 10.9193C15.7704 11.1722 15.7084 11.3913 15.6244 11.5855C15.6244 11.5855 14.6721 13.2841 14.552 13.4451C14.4185 13.6241 14.249 13.7957 13.9998 13.8972C13.7506 13.9988 13.5096 13.9943 13.2892 13.9592C13.0912 13.9277 11.9345 13.6009 11.6885 13.5309C10.9472 13.3217 10.5825 13.874 10.4929 14.1764C10.4178 14.4026 10.093 15.3754 10.0137 15.5465C9.92535 15.7371 9.804 15.9281 9.59955 16.076C9.39503 16.2239 9.1758 16.2791 8.96738 16.303C8.96738 16.303 6.96977 16.3244 6.78269 16.303C6.57424 16.2791 6.35503 16.2239 6.15048 16.076C5.94602 15.9281 5.82468 15.7371 5.73633 15.5465C5.65698 15.3754 5.33228 14.4026 5.25717 14.1764C5.00724 13.4291 4.35599 13.4347 4.0616 13.5309C4.0616 13.5309 2.6589 13.9277 2.4608 13.9592C2.24048 13.9943 1.99946 13.9988 1.75022 13.8972C1.50107 13.7957 1.33153 13.6241 1.19806 13.4451C1.07796 13.2841 0.125576 11.5855 0.125576 11.5855C0.0416231 11.3913 -0.0203104 11.1722 0.0062093 10.9193C0.0327314 10.6665 0.138814 10.465 0.261262 10.2926C0.371212 10.1377 1.45818 8.92275 1.45818 8.92275C1.93511 8.38875 1.65689 7.80315 1.45816 7.57718C1.45816 7.57718 0.371209 6.36219 0.261259 6.20736C0.138811 6.03493 0.0327299 5.83341 0.00620787 5.58059C-0.02031 5.32778 0.0416208 5.10857 0.125574 4.91441C0.125574 4.91441 1.07797 3.2158 1.19806 3.05477C1.33154 2.87579 1.50107 2.70416 1.75022 2.60268C1.99945 2.50116 2.24047 2.50563 2.46078 2.54066C2.65888 2.57216 4.06163 2.96897 4.06163 2.96897C4.87815 3.19947 5.19889 2.63471 5.25719 2.32351C5.33231 2.09732 5.65698 1.1246 5.73633 0.953445C5.82468 0.762885 5.94603 0.571892 6.15048 0.424024C6.35503 0.276089 6.57424 0.221035 6.78269 0.197155Z"
          fill="#6F41E6"
        />
        <path
          d="M7.8754 9.16992C9.20576 9.17004 10.3664 9.89359 10.9867 10.9658C11.1423 11.2348 11.0505 11.5788 10.7816 11.7344C10.5127 11.8899 10.1686 11.7982 10.0131 11.5293C9.58557 10.7903 8.78808 10.295 7.8754 10.2949C6.96265 10.2949 6.16429 10.7902 5.73673 11.5293C5.58113 11.7981 5.23704 11.8899 4.96817 11.7344C4.69947 11.5788 4.60759 11.2347 4.76309 10.9658C5.38349 9.89342 6.54485 9.16992 7.8754 9.16992ZM7.8754 4.3125C9.01426 4.3127 9.9379 5.23604 9.9379 6.375C9.9379 7.51398 9.01426 8.4373 7.8754 8.4375C6.73628 8.4375 5.8129 7.5141 5.8129 6.375C5.8129 5.23592 6.73628 4.3125 7.8754 4.3125Z"
          fill="#6F41E6"
        />
      </g>
      <defs>
        <clipPath id={clipId}>
          <rect width="15.75" height="16.5" fill="white" />
        </clipPath>
      </defs>
    </svg>
  );
};

// const GalleryIcon = () => (
//   <svg
//     width="15"
//     height="15"
//     viewBox="0 0 15 15"
//     fill="none"
//     xmlns="http://www.w3.org/2000/svg"
//   >
//     <path
//       d="M1.875 9.375L4.553 6.697C4.90356 6.34661 5.37908 6.14978 5.875 6.14978C6.37092 6.14978 6.84644 6.34661 7.197 6.697L9.875 9.375M8.625 8.125L9.428 7.322C9.77856 6.97161 10.2541 6.77478 10.75 6.77478C11.2459 6.77478 11.7214 6.97161 12.072 7.322L13.125 8.375M3.125 11.875H11.875C12.2065 11.875 12.5245 11.7433 12.7589 11.5089C12.9933 11.2745 13.125 10.9565 13.125 10.625V4.375C13.125 4.04348 12.9933 3.72554 12.7589 3.49112C12.5245 3.2567 12.2065 3.125 11.875 3.125H3.125C2.79348 3.125 2.47554 3.2567 2.24112 3.49112C2.0067 3.72554 1.875 4.04348 1.875 4.375V10.625C1.875 10.9565 2.0067 11.2745 2.24112 11.5089C2.47554 11.7433 2.79348 11.875 3.125 11.875ZM8.625 5.625H8.631V5.631H8.625V5.625ZM8.9375 5.625C8.9375 5.70788 8.90458 5.78737 8.84597 5.84597C8.78737 5.90458 8.70788 5.9375 8.625 5.9375C8.54212 5.9375 8.46263 5.90458 8.40403 5.84597C8.34542 5.78737 8.3125 5.70788 8.3125 5.625C8.3125 5.54212 8.34542 5.46263 8.40403 5.40403C8.46263 5.34542 8.54212 5.3125 8.625 5.3125C8.70788 5.3125 8.78737 5.34542 8.84597 5.40403C8.90458 5.46263 8.9375 5.54212 8.9375 5.625Z"
//       stroke="white"
//       strokeWidth="1.25"
//       strokeLinecap="round"
//       strokeLinejoin="round"
//     />
//   </svg>
// );

const BlockIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 18 18"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M3.75 3.75L14.25 14.25M15.75 9C15.75 12.7279 12.7279 15.75 9 15.75C5.27208 15.75 2.25 12.7279 2.25 9C2.25 5.27208 5.27208 2.25 9 2.25C12.7279 2.25 15.75 5.27208 15.75 9Z"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

interface EditEmployeeDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  employee: Employee | null;
  onSave: (data: EditEmployeeFormData) => Promise<void>;
  isSaving: boolean;
  onDeactivate: () => Promise<void>;
  onNavigatePrevious: () => void;
  onNavigateNext: () => void;
}

const getInitials = (firstName: string, lastName: string): string => {
  return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
};

function FormRow({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex gap-6 items-center">
      <div className="w-[82px] flex-shrink-0">
        <span className="text-[13px] font-medium text-[#31394a] leading-[17px]">
          {label}
        </span>
      </div>
      <div className="flex-1">{children}</div>
    </div>
  );
}

function PermissionsSection({
  selectedRole,
  onRoleChange,
  error,
}: {
  selectedRole: string;
  onRoleChange: (role: string) => void;
  error?: string;
}) {
  const containerStyles = useRadioGroupContainerStyles({
    selectedValue: selectedRole,
    firstValue: "SALES_REPRESENTATIVE",
    lastValue: "GENERAL_MANAGER",
    adjacentToBottom: true,
  });

  return (
    <div>
      <div
        className={`bg-[#f3f5f8] pb-[6px] px-[6px] transition-shadow duration-200 ${error ? "border-[3px] border-[#872525] shadow-[0_0_0_4px_rgba(135,37,37,0.16)]" : " border-[1px] border-[#e4e7ec]"}`}
        style={containerStyles.style}
      >
        <div className="px-4 pt-[22px] pb-4">
          <h3 className="text-[14px] font-medium text-[#111827] leading-none">
            Permissions
          </h3>
          <p className="text-[12px] font-normal text-[#4a5466] leading-none mt-2">
            Specify the role this user holds at the dealership.
          </p>
        </div>
        <RadioGroup value={selectedRole} onValueChange={onRoleChange}>
          <RadioGroupCardItem
            value="SALES_REPRESENTATIVE"
            icon={<SalesRepresentativeIcon />}
            iconBgColor="bg-transparent"
            label="Sales Representative"
            description="Access to assigned deals only."
          />
          <RadioGroupCardItem
            value="GENERAL_MANAGER"
            icon={<GeneralManagerIcon />}
            iconBgColor="bg-[#f1edfb]"
            label="General Manager"
            description="Full access to all deals, data, dealer settings, and management."
          />
        </RadioGroup>
      </div>
      {error && (
        <p className="mt-2 text-[12px] font-normal text-[#872525] leading-4">
          {error}
        </p>
      )}
    </div>
  );
}

export default function EditEmployeeDrawer({
  open,
  onOpenChange,
  employee,
  onSave,
  isSaving,
  onDeactivate,
  onNavigatePrevious,
  onNavigateNext,
}: EditEmployeeDrawerProps) {
  const [formData, setFormData] = useState<EditEmployeeFormState>({
    first_name: "",
    last_name: "",
    email: "",
    phone_number: "",
    title: "",
    role: "",
  });
  const [photoDialogOpen, setPhotoDialogOpen] = useState(false);
  const [selectedPhoto, setSelectedPhoto] = useState<string | null>(null);
  const [isDeactivating, setIsDeactivating] = useState(false);
  const [deactivateDialogOpen, setDeactivateDialogOpen] = useState(false);

  // Form validation using Zod schema
  const { validate, fieldErrors, clearFieldError, clearAllErrors } =
    useFormValidation(editEmployeeSchema, {
      validationErrorToast: "Please fix the errors in the form",
    });

  useEffect(() => {
    if (employee) {
      setFormData({
        first_name: employee.first_name || "",
        last_name: employee.last_name || "",
        email: employee.email || "",
        phone_number: employee.phone_number || "",
        title: employee.title || "",
        role: employee.role || "",
      });
      clearAllErrors();
      // setSelectedPhoto((employee.profile_photo_url || null);
    }
  }, [employee, clearAllErrors]);

  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) {
      clearAllErrors();
    }
    onOpenChange(isOpen);
  };

  const handleNavigateNext = () => {
    clearAllErrors();
    onNavigateNext();
  };

  const handleNavigatePrevious = () => {
    clearAllErrors();
    onNavigatePrevious();
  };

  const handleFieldChange = (
    field: keyof EditEmployeeFormState,
    value: string,
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    clearFieldError(field);
  };

  const handleSave = async () => {
    const result = validate(formData);
    if (!result.success) {
      return;
    }

    await onSave(result.data);
  };

  const handleDeactivate = async () => {
    setIsDeactivating(true);
    try {
      await onDeactivate();
      setDeactivateDialogOpen(false);
    } finally {
      setIsDeactivating(false);
    }
  };

  const handleOpenDeactivateDialog = () => {
    setDeactivateDialogOpen(true);
  };

  const handlePhotoSelect = (photoUrl: string | null) => {
    setSelectedPhoto(photoUrl);
    setPhotoDialogOpen(false);
  };

  const displayName =
    `${formData.first_name} ${formData.last_name}`.trim() || "Employee";

  const ChevronRightIcon = ({ className }: { className?: string }) => (
    <svg
      width="12"
      height="12"
      viewBox="0 0 3.74993 6.74993"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={cn("w-2 h-2 shrink-0", className ?? "")}
    >
      <path
        d="M0.10962 0.109615C0.25592 -0.0366844 0.493412 -0.0363921 0.639894 0.109615L3.63989 3.10961C3.78634 3.25606 3.78634 3.49344 3.63989 3.63989L0.639894 6.63989C0.493447 6.78633 0.256066 6.78633 0.10962 6.63989C-0.0363983 6.49341 -0.0366816 6.25592 0.10962 6.10961L2.84497 3.37524L0.10962 0.639888C-0.0363826 0.493411 -0.0366657 0.255916 0.10962 0.109615Z"
        fill="#31394A"
      />
    </svg>
  );

  const PersonIcon = () => (
    <svg
      width="12"
      height="12"
      viewBox="0 0 10.75 9.75"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className="shrink-0"
    >
      <path
        d="M5.89746 1.79832e-07C6.81554 -3.96183e-06 7.53543 -0.000309075 8.10645 0.0615236C8.68901 0.12464 9.16139 0.256132 9.56445 0.548828C9.80853 0.726157 10.0238 0.941475 10.2012 1.18555C10.4939 1.58863 10.6254 2.06096 10.6885 2.64355C10.7503 3.21457 10.75 3.93443 10.75 4.85254V4.89746C10.75 5.8155 10.7503 6.53545 10.6885 7.10645C10.6253 7.68921 10.4941 8.16227 10.2012 8.56543C10.0239 8.80942 9.80846 9.02389 9.56445 9.20117C9.16137 9.4939 8.68904 9.62536 8.10645 9.68848C7.53543 9.75031 6.81554 9.75 5.89746 9.75H4.85254C3.93446 9.75 3.21457 9.75031 2.64355 9.68848C2.06096 9.62536 1.58863 9.4939 1.18555 9.20117C0.941542 9.02389 0.726129 8.80942 0.548828 8.56543C0.255914 8.16227 0.124663 7.68921 0.0615236 7.10645C-0.000303343 6.53544 -3.9573e-06 5.81551 1.84226e-07 4.89746V4.85254C-3.9576e-06 3.93443 -0.000316421 3.21458 0.0615236 2.64355C0.124644 2.06096 0.256097 1.58863 0.548828 1.18555C0.726157 0.941475 0.941475 0.726157 1.18555 0.548828C1.58862 0.256132 2.06099 0.12464 2.64355 0.0615236C3.21457 -0.000309242 3.93446 -3.96183e-06 4.85254 1.79832e-07H5.89746ZM4.875 0.75C3.92921 0.75 3.24961 0.750632 2.72363 0.807617C2.20491 0.863854 1.87991 0.971753 1.62598 1.15625C1.44571 1.28724 1.28725 1.44572 1.15625 1.62598C0.971761 1.8799 0.863856 2.20494 0.807617 2.72363C0.750633 3.2496 0.75 3.92925 0.75 4.875C0.75 5.82079 0.750632 6.50039 0.807617 7.02637C0.863853 7.54513 0.971747 7.87008 1.15625 8.12402C1.28725 8.3043 1.44571 8.46275 1.62598 8.59375C1.87991 8.77825 2.20491 8.88615 2.72363 8.94238C3.24961 8.99937 3.92921 9 4.875 9H5.875C6.82079 9 7.50039 8.99937 8.02637 8.94238C8.54509 8.88615 8.87009 8.77825 9.12402 8.59375C9.30429 8.46275 9.46275 8.3043 9.59375 8.12402C9.77825 7.87008 9.88615 7.54513 9.94238 7.02637C9.99937 6.50039 10 5.82079 10 4.875C10 3.92925 9.99937 3.2496 9.94238 2.72363C9.88614 2.20494 9.77824 1.8799 9.59375 1.62598C9.46275 1.44572 9.30429 1.28724 9.12402 1.15625C8.87009 0.971753 8.5451 0.863854 8.02637 0.807617C7.50039 0.750632 6.82079 0.75 5.875 0.75H4.875ZM3.60449 5.5C4.4266 5.48786 5.31345 5.85857 5.71484 6.71582C5.80265 6.90339 5.72174 7.12703 5.53418 7.21484C5.34661 7.30265 5.12297 7.22175 5.03516 7.03418C4.79261 6.51609 4.23106 6.24091 3.61523 6.25C2.99503 6.2593 2.43904 6.55528 2.21484 7.03418C2.12702 7.22171 1.90337 7.30264 1.71582 7.21484C1.52837 7.12698 1.44737 6.90334 1.53516 6.71582C1.91506 5.90441 2.78698 5.51212 3.60449 5.5ZM7.875 4.75C8.08209 4.75 8.24997 4.91792 8.25 5.125C8.25 5.33211 8.08211 5.5 7.875 5.5H6.375C6.16789 5.5 6 5.33211 6 5.125C6.00003 4.91792 6.16791 4.75 6.375 4.75H7.875ZM3.625 2C4.38437 2 4.99997 2.61564 5 3.375C5 4.13439 4.38439 4.75 3.625 4.75C2.86561 4.75 2.25 4.13439 2.25 3.375C2.25003 2.61564 2.86563 2 3.625 2ZM3.625 2.75C3.27984 2.75 3.00003 3.02985 3 3.375C3 3.72018 3.27982 4 3.625 4C3.97018 4 4.25 3.72018 4.25 3.375C4.24997 3.02985 3.97016 2.75 3.625 2.75ZM8.375 3C8.58209 3 8.74997 3.16792 8.75 3.375C8.75 3.58211 8.58211 3.75 8.375 3.75H6.375C6.16789 3.75 6 3.58211 6 3.375C6.00003 3.16792 6.16791 3 6.375 3H8.375Z"
        fill="#31394A"
      />
    </svg>
  );

  // ChevronDown icon - exact match from Figma
  // const ChevronDownPersonIcon = () => (
  //   <svg
  //     width="12"
  //     height="12"
  //     viewBox="0 0 6.74986 3.74986"
  //     fill="none"
  //     xmlns="http://www.w3.org/2000/svg"
  //     className="w-2 h-2 shrink-0"
  //   >
  //     <path
  //       d="M6.10975 0.109753C6.25615 -0.036628 6.49357 -0.036541 6.64003 0.109753C6.78647 0.256195 6.78646 0.493578 6.64003 0.640027L3.64003 3.64003C3.49358 3.78647 3.2562 3.78647 3.10975 3.64003L0.109754 0.640027C-0.0365323 0.493568 -0.0366373 0.256148 0.109754 0.109753C0.25615 -0.036612 0.493577 -0.0365252 0.640028 0.109753L3.37538 2.84511L6.10975 0.109753Z"
  //       fill="#31394A"
  //     />
  //   </svg>
  // );

  const ChevronDownIcon = () => (
    <svg
      width="16"
      height="16"
      viewBox="0 0 8.99989 4.9999"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className="w-2 h-2 shrink-0"
    >
      <path
        d="M8.14645 0.146428C8.34171 -0.048798 8.65823 -0.0488209 8.85348 0.146428C9.0487 0.341681 9.04869 0.658205 8.85348 0.85346L4.85348 4.85346C4.65822 5.04872 4.34171 5.04871 4.14645 4.85346L0.146447 0.85346C-0.0488171 0.658199 -0.048814 0.341692 0.146447 0.146428C0.341712 -0.0487815 0.658232 -0.0488144 0.853478 0.146428L4.49996 3.79291L8.14645 0.146428Z"
        fill="#4A5466"
      />
    </svg>
  );

  const ChevronUpIcon = () => (
    <svg
      width="16"
      height="16"
      viewBox="0 0 8.99991 4.99991"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className="w-2 h-2 shrink-0"
    >
      <path
        d="M4.14645 0.146447C4.34171 -0.0488155 4.65822 -0.0488155 4.85348 0.146447L8.85348 4.14645C9.0487 4.34171 9.04873 4.65823 8.85348 4.85348C8.65823 5.04873 8.34171 5.0487 8.14645 4.85348L4.49996 1.20699L0.853478 4.85348C0.658228 5.04873 0.341712 5.0487 0.146447 4.85348C-0.0488155 4.65822 -0.0488155 4.34171 0.146447 4.14645L4.14645 0.146447Z"
        fill="#4A5466"
      />
    </svg>
  );

  return (
    <>
      <Drawer open={open} onOpenChange={handleOpenChange} direction="right">
        <DrawerContent
          className="w-[588px] max-w-[588px] overflow-hidden"
          aria-describedby={undefined}
        >
          <VisuallyHidden>
            <DrawerTitle>Edit Employee: {displayName}</DrawerTitle>
          </VisuallyHidden>
          <div className="flex justify-between items-center px-4 py-3 border-b border-[#e4e7ec]">
            <div className="flex gap-1 items-center">
              <div className="flex gap-1 items-center p-2 h-6 rounded-lg">
                <span className="text-[12px] font-normal text-[#4a5466] leading-4">
                  Employees
                </span>
                <ChevronRightIcon className="mx-1" />
              </div>
              <div className="bg-[#f3f5f8] flex gap-1 h-6 items-center p-2 rounded-lg">
                <PersonIcon />
                <span className="text-[12px] font-normal text-[#111827] leading-4">
                  {displayName}
                </span>
                {/* <ChevronDownIcon /> */}
              </div>
            </div>
            <div className="flex gap-1 items-center">
              <Button
                variant="ghost"
                size="icon"
                onClick={handleNavigateNext}
                className="!w-8 !h-8 !p-0 rounded-full"
                innerClassName="!w-full !h-full flex items-center justify-center"
              >
                <ChevronDownIcon />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={handleNavigatePrevious}
                className="!w-8 !h-8 !p-0 rounded-full up"
                innerClassName="!w-full !h-full flex items-center justify-center"
              >
                <ChevronUpIcon />
              </Button>
              <DrawerClose asChild>
                <Button variant="ghost-blue" size="xs">
                  Close
                  <X className="w-3 h-3" />
                </Button>
              </DrawerClose>
            </div>
          </div>

          <div className="overflow-y-auto flex-1 pb-6 space-y-6">
            <div className="relative h-[182px] overflow-hidden bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50">
              <div className="absolute -top-20 -right-20 w-64 h-64 bg-gradient-to-b rounded-full blur-2xl from-blue-200/40 to-purple-200/40" />
              <div className="absolute -left-20 -bottom-32 w-80 h-80 bg-gradient-to-t rounded-full blur-3xl from-pink-200/30 to-blue-200/30" />

              <div className="flex absolute inset-0 flex-col justify-center items-center">
                <div className="relative">
                  <Avatar className="w-[88px] h-[88px] border-4 border-white shadow-md">
                    {selectedPhoto ? (
                      <AvatarImage src={selectedPhoto} alt={displayName} />
                    ) : null}
                    <AvatarFallback className="text-2xl font-semibold text-blue-600 bg-blue-100">
                      {getInitials(
                        formData.first_name || "N",
                        formData.last_name || "E",
                      )}
                    </AvatarFallback>
                  </Avatar>
                  {/* <button
                    onClick={() => setPhotoDialogOpen(true)}
                    className="flex absolute right-0 bottom-0 justify-center items-center w-8 h-8 rounded-full border-[1.88px] border-[#f3f5f8] bg-gradient-to-b from-[#3c6ff4] to-[#3265ea] shadow-sm transition-colors hover:from-[#3565e0] hover:to-[#2d5bd8]"
                  >
                    <GalleryIcon />
                  </button> */}
                </div>
                <div className="mt-4 text-center">
                  <h2 className="text-sm font-semibold text-grey-900">
                    {displayName}
                  </h2>
                  <p className="mt-1 text-xs text-[#4A5466]">
                    {formData.email}
                  </p>
                </div>
              </div>
            </div>

            <div className="px-6 space-y-6">
              <div className="bg-[#f3f5f8] border border-[#e4e7ec] rounded-[14px] pb-[6px] px-[6px]">
                <div className="px-4 pt-[22px] pb-4">
                  <h3 className="text-[14px] font-medium text-[#111827] leading-none">
                    Account details
                  </h3>
                  <p className="text-[12px] font-normal text-[#4a5466] leading-none mt-2">
                    Employee personal information.
                  </p>
                </div>
                <div className="bg-white border border-[#e4e7ec] rounded-lg px-6 pt-6 pb-4 space-y-2">
                  <FormRow label="First name">
                    <Input
                      value={formData.first_name}
                      onChange={(e) =>
                        handleFieldChange("first_name", e.target.value)
                      }
                      size="s"
                      error={!!fieldErrors.first_name}
                      helperText={fieldErrors.first_name}
                    />
                  </FormRow>
                  <FormRow label="Last name">
                    <Input
                      value={formData.last_name}
                      onChange={(e) =>
                        handleFieldChange("last_name", e.target.value)
                      }
                      size="s"
                      error={!!fieldErrors.last_name}
                      helperText={fieldErrors.last_name}
                    />
                  </FormRow>
                  <FormRow label="Email">
                    <Input
                      value={formData.email}
                      onChange={(e) =>
                        handleFieldChange("email", e.target.value)
                      }
                      size="s"
                      type="email"
                      error={!!fieldErrors.email}
                      helperText={fieldErrors.email}
                    />
                  </FormRow>
                  <FormRow label="Phone">
                    <PhoneInput
                      value={formData.phone_number}
                      onChange={(e) => handleFieldChange("phone_number", e)}
                      size="s"
                      showOptional
                      error={!!fieldErrors.phone_number}
                      helperText={fieldErrors.phone_number}
                    />
                  </FormRow>
                  <FormRow label="Title">
                    <Input
                      value={formData.title}
                      onChange={(e) =>
                        handleFieldChange("title", e.target.value)
                      }
                      size="s"
                      error={!!fieldErrors.title}
                      helperText={fieldErrors.title}
                    />
                  </FormRow>
                  {employee?.last_activity && (
                    <FormRow label="Last active">
                      <div className="flex gap-1 items-center h-[50px]">
                        <Zap className="w-4 h-4 text-yellow-500" />
                        <span className="text-[14px] font-medium text-[#111827]">
                          {timeAgo(employee.last_activity)}
                        </span>
                      </div>
                    </FormRow>
                  )}
                </div>
              </div>

              <PermissionsSection
                selectedRole={formData.role}
                onRoleChange={(role) => handleFieldChange("role", role)}
                error={fieldErrors.role}
              />

              <div className="space-y-4">
                <p className="text-[12px] font-medium text-[#4a5466] leading-none">
                  Danger zone
                </p>
                <Button
                  variant="destructive-secondary"
                  size="m"
                  onClick={handleOpenDeactivateDialog}
                  disabled={isDeactivating || isSaving}
                  className="w-full"
                >
                  <BlockIcon />
                  Deactivate account
                </Button>
              </div>
            </div>
          </div>

          <div className="flex gap-3 justify-end items-center px-6 py-4 bg-white border-t border-grey-200">
            <DrawerClose asChild>
              <Button
                variant="ghost"
                size="s"
                disabled={isSaving || isDeactivating}
              >
                Cancel
              </Button>
            </DrawerClose>
            <Button
              variant="primary"
              size="s"
              onClick={() => void handleSave()}
              disabled={isSaving || isDeactivating}
              loading={isSaving}
            >
              Save Changes
            </Button>
          </div>
        </DrawerContent>
      </Drawer>

      <ProfilePhotoDialog
        open={photoDialogOpen}
        onOpenChange={setPhotoDialogOpen}
        currentPhoto={selectedPhoto}
        onPhotoSelect={handlePhotoSelect}
        employeeName={displayName}
      />

      <DeactivateEmployeeDialog
        open={deactivateDialogOpen}
        onOpenChange={setDeactivateDialogOpen}
        employeeName={displayName}
        onConfirm={handleDeactivate}
      />
    </>
  );
}
